export type Locale = 'pt-BR' | 'en' | 'es';

export interface LoginKitMessages {
  form: {
    title: string;
    lead: string;
    password: string;
    forgotPassword: string;
    showPassword: string;
    hidePassword: string;
    secure2fa: string;
    login: string;
    loggingIn: string;
    done: string;
    continueWith: string;
    magicLink: string;
    backToPassword: string;
    sentTitle: string;
    sentPrefix: string;
    sentFallbackEmail: string;
    sentSuffix: string;
    backToLogin: string;
    /** Ação primária de um bloqueio recuperável (ex.: as permissões não carregaram). */
    tryAgain: string;
  };
  modal: {
    close: string;
    title: string;
    lead: string;
    identifierLabel: string;
    sending: string;
    sendRecoveryLink: string;
    rememberedPassword: string;
    backToLogin: string;
    sentTitle: string;
    sentPrefix: string;
    sentSuffix: string;
    understood: string;
    tryAnotherEmail: string;
    error: string;
    /** Passo 2 — inserir o código recebido + definir a nova senha. */
    verifyTitle: string;
    /** Contém o marcador `{email}`. */
    verifyLead: string;
    codeLabel: string;
    codePlaceholder: string;
    codeRequired: string;
    codeInvalid: string;
    newPasswordLabel: string;
    weakPassword: string;
    verifySubmit: string;
    verifySubmitting: string;
    resendCode: string;
    emailNotFound: string;
    resetDoneTitle: string;
    resetDoneText: string;
    resetDoneCta: string;
  };
  /** Mensagens das notificações (banner de erro / sucesso). */
  notice: {
    /** Título curto exibido em negrito. */
    errorTitle: string;
    successTitle: string;
    dismiss: string;
    /** Falhas de login mapeadas por situação. */
    invalidCredentials: string;
    /** O Clerk aceitou as credenciais mas a conta exige definir uma senha nova. */
    needsNewPassword: string;
    /**
     * A conta existe mas não tem senha utilizável — não é senha errada, e mandar conferir os dados só
     * atrasa quem já digitou tudo certo. Acontece com quem criou o acesso pelo portal hospedado do Clerk.
     */
    passwordNotSet: string;
    /** A conta exige segundo fator, que esta tela ainda não implementa. */
    needsSecondFactor: string;
    tooManyAttempts: string;
    serverError: string;
    networkError: string;
    loginError: string;
    loginSuccess: string;
    magicLinkError: string;
    /** O link mágico foi aberto em outro navegador ou aparelho do que pediu. */
    magicLinkClientMismatch: string;
    /** O link mágico expirou ou já foi usado. */
    magicLinkExpired: string;
    /** Já existe uma identidade ativa no navegador — a senha nunca foi o problema. */
    alreadySignedIn: string;
    fixFields: string;
  };
  /** Mensagens de validação de inputs. `{min}` é interpolado. */
  validation: {
    required: string;
    email: string;
    cnpjOrEmail: string;
    passwordRequired: string;
    passwordMin: string;
  };
  switcher: {
    trigger: string;
    head: string;
    current: string;
    language: string;
  };
  brand: {
    footPhrase: string;
    rights: string;
  };
  /** The "stage" login's own copy (see stage/LoginStage.tsx). */
  stage: {
    /** The floating cards around the mark — the public site's hero claims. */
    chips: { title: string; detail: string }[];
    /** The running band at the foot of the stage. */
    marquee: string[];
    /** Accessible label of the button that skips the opening sequence. */
    skipIntro: string;
  };
  legal: {
    prefix: string;
    terms: string;
    middle: string;
    privacy: string;
    suffix: string;
  };
}

const messages: Record<Locale, LoginKitMessages> = {
  'pt-BR': {
    form: {
      title: 'Entrar',
      lead: 'Bom ter você de volta. Acesse sua conta para continuar.',
      password: 'Senha',
      forgotPassword: 'Esqueci minha senha',
      showPassword: 'Mostrar',
      hidePassword: 'Ocultar',
      secure2fa: 'Dados de acesso protegidos, criptografados e seguros.',
      login: 'Entrar',
      loggingIn: 'Entrando...',
      done: 'Pronto',
      continueWith: 'ou continue com',
      magicLink: 'Entrar com link mágico',
      backToPassword: 'Voltar para e-mail e senha',
      sentTitle: 'Link enviado',
      sentPrefix: 'Enviamos um link de acesso para',
      sentFallbackEmail: 'seu e-mail',
      sentSuffix: 'Abra no seu dispositivo para entrar com segurança.',
      backToLogin: 'Voltar ao login',
      tryAgain: 'Tentar de novo',
    },
    modal: {
      close: 'Fechar',
      title: 'Recuperar acesso',
      lead: 'Informe o e-mail da sua conta e enviaremos um link seguro para você criar uma nova senha.',
      identifierLabel: 'E-mail',
      sending: 'Enviando...',
      sendRecoveryLink: 'Enviar link de recuperação',
      rememberedPassword: 'Lembrou a senha?',
      backToLogin: 'Voltar ao login',
      sentTitle: 'Verifique seu e-mail',
      sentPrefix: 'Se existir uma conta para',
      sentSuffix: 'enviamos um link para você criar uma nova senha. O link expira em 30 minutos.',
      understood: 'Entendi',
      tryAnotherEmail: 'Não recebi - tentar outro e-mail',
      error: 'Não foi possível enviar o link de recuperação. Tente novamente.',
      verifyTitle: 'Criar nova senha',
      verifyLead: 'Enviamos um código de 6 dígitos para {email}. Informe-o abaixo e defina sua nova senha.',
      codeLabel: 'Código de verificação',
      codePlaceholder: '000000',
      codeRequired: 'Informe o código que enviamos.',
      codeInvalid: 'Código inválido ou expirado. Confira e tente novamente.',
      newPasswordLabel: 'Nova senha',
      weakPassword: 'Senha muito fraca ou já vazada. Escolha uma senha mais forte.',
      verifySubmit: 'Redefinir senha',
      verifySubmitting: 'Redefinindo...',
      resendCode: 'Reenviar código',
      emailNotFound: 'Não encontramos uma conta com esse e-mail.',
      resetDoneTitle: 'Senha redefinida',
      resetDoneText: 'Sua senha foi atualizada e você já está conectado.',
      resetDoneCta: 'Continuar',
    },
    notice: {
      errorTitle: 'Não foi possível entrar',
      successTitle: 'Tudo certo',
      dismiss: 'Fechar aviso',
      invalidCredentials: 'E-mail ou senha incorretos. Confira os dados e tente novamente.',
      needsNewPassword: 'Esta conta precisa de uma senha nova. Use "Esqueci minha senha" para defini-la.',
      passwordNotSet: 'Esta conta ainda não tem senha para entrar. Use "Esqueci minha senha" para criar a sua.',
      needsSecondFactor: 'Esta conta exige verificação em duas etapas, ainda não disponível neste portal.',
      tooManyAttempts: 'Muitas tentativas seguidas. Aguarde um instante antes de tentar de novo.',
      serverError: 'Estamos com instabilidade no momento. Tente novamente em alguns instantes.',
      networkError: 'Sem conexão. Verifique sua internet e tente novamente.',
      loginError: 'Não foi possível concluir o acesso. Tente novamente.',
      loginSuccess: 'Acesso liberado. Redirecionando...',
      magicLinkError: 'Não foi possível enviar o link de acesso. Tente novamente.',
      magicLinkClientMismatch: 'O link precisa ser aberto no mesmo navegador em que você o pediu. Volte para esta aba e peça um link novo.',
      magicLinkExpired: 'Este link de acesso expirou ou já foi usado. Peça um novo.',
      alreadySignedIn: 'Você já está identificado neste navegador. Recarregue a página para continuar, ou saia para entrar com outra conta.',
      fixFields: 'Revise os campos destacados para continuar.',
    },
    validation: {
      required: 'Campo obrigatório.',
      email: 'Informe um e-mail válido.',
      cnpjOrEmail: 'Informe um CNPJ válido (00.000.000/0000-00) ou um e-mail.',
      passwordRequired: 'Informe sua senha.',
      passwordMin: 'A senha deve ter ao menos {min} caracteres.',
    },
    switcher: {
      trigger: 'Portais TMR',
      head: 'Acessar outro portal',
      current: 'Atual',
      language: 'Idioma',
    },
    brand: {
      footPhrase: 'Mais receita, menos dor de cabeça',
      rights: '© TMR - Todos os direitos reservados',
    },
    stage: {
      chips: [
        { title: 'Análise de Aprovação', detail: 'feita pela TMR' },
        { title: 'Repasse garantido', detail: 'direto na conta da clínica' },
        { title: 'Boleto pago', detail: 'cobrança humanizada' },
      ],
      marquee: [
        'Parcelamento em até 36x no boleto',
        'Análise de aprovação',
        'Cobrança humanizada',
        'Repasse garantido',
        'Contrato com assinatura digital',
        'Sem usar o limite do cartão',
      ],
      skipIntro: 'Pular abertura',
    },
    legal: {
      prefix: 'Ao continuar você concorda com os',
      terms: 'Termos',
      middle: 'e a',
      privacy: 'Política de privacidade',
      suffix: '.',
    },
  },
  en: {
    form: {
      title: 'Sign in',
      lead: 'Great to have you back. Access your account to continue.',
      password: 'Password',
      forgotPassword: 'Forgot password',
      showPassword: 'Show',
      hidePassword: 'Hide',
      secure2fa: 'Data access protected, encrypted and secure.',
      login: 'Sign in',
      loggingIn: 'Signing in...',
      done: 'Done',
      continueWith: 'or continue with',
      magicLink: 'Sign in with magic link',
      backToPassword: 'Back to email and password',
      sentTitle: 'Link sent',
      sentPrefix: 'We sent an access link to',
      sentFallbackEmail: 'your email',
      sentSuffix: 'Open it on your device to sign in securely.',
      backToLogin: 'Back to login',
      tryAgain: 'Try again',
    },
    modal: {
      close: 'Close',
      title: 'Recover access',
      lead: 'Enter your account email and we will send a secure link so you can create a new password.',
      identifierLabel: 'Email',
      sending: 'Sending...',
      sendRecoveryLink: 'Send recovery link',
      rememberedPassword: 'Remembered your password?',
      backToLogin: 'Back to login',
      sentTitle: 'Check your email',
      sentPrefix: 'If an account exists for',
      sentSuffix: 'we sent a link so you can create a new password. The link expires in 30 minutes.',
      understood: 'Got it',
      tryAnotherEmail: 'Did not get it - try another email',
      error: 'We could not send the recovery link. Please try again.',
      verifyTitle: 'Create new password',
      verifyLead: 'We sent a 6-digit code to {email}. Enter it below and set your new password.',
      codeLabel: 'Verification code',
      codePlaceholder: '000000',
      codeRequired: 'Enter the code we sent.',
      codeInvalid: 'Invalid or expired code. Check it and try again.',
      newPasswordLabel: 'New password',
      weakPassword: 'Password too weak or previously leaked. Choose a stronger one.',
      verifySubmit: 'Reset password',
      verifySubmitting: 'Resetting...',
      resendCode: 'Resend code',
      emailNotFound: 'We could not find an account with that email.',
      resetDoneTitle: 'Password reset',
      resetDoneText: 'Your password has been updated and you are now signed in.',
      resetDoneCta: 'Continue',
    },
    notice: {
      errorTitle: 'Could not sign in',
      successTitle: 'All set',
      dismiss: 'Dismiss notice',
      invalidCredentials: 'Incorrect email or password. Check your details and try again.',
      needsNewPassword: 'This account needs a new password. Use "Forgot my password" to set one.',
      passwordNotSet: 'This account has no sign-in password yet. Use "Forgot my password" to create one.',
      needsSecondFactor: 'This account requires two-step verification, not available in this portal yet.',
      tooManyAttempts: 'Too many attempts. Please wait a moment before trying again.',
      serverError: 'We are experiencing issues right now. Please try again shortly.',
      networkError: 'No connection. Check your internet and try again.',
      loginError: 'We could not complete sign in. Please try again.',
      loginSuccess: 'Access granted. Redirecting...',
      magicLinkError: 'We could not send the access link. Please try again.',
      magicLinkClientMismatch: 'The link must be opened in the same browser you requested it from. Come back to this tab and request a new one.',
      magicLinkExpired: 'This access link has expired or was already used. Please request a new one.',
      alreadySignedIn: 'You are already identified in this browser. Reload the page to continue, or sign out to use another account.',
      fixFields: 'Review the highlighted fields to continue.',
    },
    validation: {
      required: 'This field is required.',
      email: 'Enter a valid email.',
      cnpjOrEmail: 'Enter a valid CNPJ (00.000.000/0000-00) or an email.',
      passwordRequired: 'Enter your password.',
      passwordMin: 'Password must be at least {min} characters.',
    },
    switcher: {
      trigger: 'TMR Portals',
      head: 'Access another portal',
      current: 'Current',
      language: 'Language',
    },
    brand: {
      footPhrase: 'More revenue, less headache',
      rights: '© TMR - All rights reserved',
    },
    stage: {
      chips: [
        { title: 'Approval review', detail: 'done by TMR' },
        { title: 'Guaranteed payout', detail: "straight to the clinic's account" },
        { title: 'Installment paid', detail: 'human-centered collection' },
      ],
      marquee: [
        'Up to 36 installments by bank slip',
        'Approval review',
        'Human-centered collection',
        'Guaranteed payout',
        'Digitally signed contract',
        'No card limit needed',
      ],
      skipIntro: 'Skip intro',
    },
    legal: {
      prefix: 'By continuing you agree to the',
      terms: 'Terms',
      middle: 'and',
      privacy: 'Privacy policy',
      suffix: '.',
    },
  },
  es: {
    form: {
      title: 'Iniciar sesion',
      lead: 'Que bueno tenerte de vuelta. Accede a tu cuenta para continuar.',
      password: 'Contrasena',
      forgotPassword: 'Olvide mi contrasena',
      showPassword: 'Mostrar',
      hidePassword: 'Ocultar',
      secure2fa: 'Datos de acceso protegidos, criptografados y seguros.',
      login: 'Entrar',
      loggingIn: 'Ingresando...',
      done: 'Listo',
      continueWith: 'o continua con',
      magicLink: 'Entrar con enlace magico',
      backToPassword: 'Volver a correo y contrasena',
      sentTitle: 'Enlace enviado',
      sentPrefix: 'Enviamos un enlace de acceso a',
      sentFallbackEmail: 'tu correo',
      sentSuffix: 'Abrelo en tu dispositivo para entrar de forma segura.',
      backToLogin: 'Volver al inicio',
      tryAgain: 'Intentar de nuevo',
    },
    modal: {
      close: 'Cerrar',
      title: 'Recuperar acceso',
      lead: 'Ingresa el correo de tu cuenta y te enviaremos un enlace seguro para crear una nueva contrasena.',
      identifierLabel: 'Correo',
      sending: 'Enviando...',
      sendRecoveryLink: 'Enviar enlace de recuperacion',
      rememberedPassword: 'Recordaste tu contrasena?',
      backToLogin: 'Volver al inicio',
      sentTitle: 'Revisa tu correo',
      sentPrefix: 'Si existe una cuenta para',
      sentSuffix: 'enviamos un enlace para crear una nueva contrasena. El enlace vence en 30 minutos.',
      understood: 'Entendido',
      tryAnotherEmail: 'No lo recibi - probar otro correo',
      error: 'No pudimos enviar el enlace de recuperacion. Intentalo de nuevo.',
      verifyTitle: 'Crear nueva contrasena',
      verifyLead: 'Enviamos un codigo de 6 digitos a {email}. Ingresalo abajo y define tu nueva contrasena.',
      codeLabel: 'Codigo de verificacion',
      codePlaceholder: '000000',
      codeRequired: 'Ingresa el codigo que enviamos.',
      codeInvalid: 'Codigo invalido o expirado. Revisalo e intenta de nuevo.',
      newPasswordLabel: 'Nueva contrasena',
      weakPassword: 'Contrasena muy debil o filtrada. Elige una mas fuerte.',
      verifySubmit: 'Restablecer contrasena',
      verifySubmitting: 'Restableciendo...',
      resendCode: 'Reenviar codigo',
      emailNotFound: 'No encontramos una cuenta con ese correo.',
      resetDoneTitle: 'Contrasena restablecida',
      resetDoneText: 'Tu contrasena fue actualizada y ya iniciaste sesion.',
      resetDoneCta: 'Continuar',
    },
    notice: {
      errorTitle: 'No se pudo iniciar sesion',
      successTitle: 'Listo',
      dismiss: 'Cerrar aviso',
      invalidCredentials: 'Correo o contrasena incorrectos. Revisa los datos e intenta de nuevo.',
      needsNewPassword: 'Esta cuenta necesita una contrasena nueva. Usa "Olvide mi contrasena" para definirla.',
      passwordNotSet: 'Esta cuenta aun no tiene contrasena de acceso. Usa "Olvide mi contrasena" para crearla.',
      needsSecondFactor: 'Esta cuenta exige verificacion en dos pasos, aun no disponible en este portal.',
      tooManyAttempts: 'Demasiados intentos. Espera un momento antes de volver a intentar.',
      serverError: 'Estamos con problemas en este momento. Intenta de nuevo en unos instantes.',
      networkError: 'Sin conexion. Verifica tu internet e intenta de nuevo.',
      loginError: 'No pudimos completar el acceso. Intentalo de nuevo.',
      loginSuccess: 'Acceso concedido. Redirigiendo...',
      magicLinkError: 'No pudimos enviar el enlace de acceso. Intentalo de nuevo.',
      magicLinkClientMismatch: 'El enlace debe abrirse en el mismo navegador desde el que lo pediste. Vuelve a esta pestana y pide uno nuevo.',
      magicLinkExpired: 'Este enlace de acceso expiro o ya fue usado. Pide uno nuevo.',
      alreadySignedIn: 'Ya estas identificado en este navegador. Recarga la pagina para continuar, o sal para entrar con otra cuenta.',
      fixFields: 'Revisa los campos resaltados para continuar.',
    },
    validation: {
      required: 'Campo obligatorio.',
      email: 'Ingresa un correo valido.',
      cnpjOrEmail: 'Ingresa un CNPJ valido (00.000.000/0000-00) o un correo.',
      passwordRequired: 'Ingresa tu contrasena.',
      passwordMin: 'La contrasena debe tener al menos {min} caracteres.',
    },
    switcher: {
      trigger: 'Portales TMR',
      head: 'Acceder a otro portal',
      current: 'Actual',
      language: 'Idioma',
    },
    brand: {
      footPhrase: 'Mas ingresos, menos dolor de cabeza',
      rights: '© TMR - Todos los derechos reservados',
    },
    stage: {
      chips: [
        { title: 'Análisis de aprobación', detail: 'hecho por TMR' },
        { title: 'Pago garantizado', detail: 'directo en la cuenta de la clínica' },
        { title: 'Boleto pagado', detail: 'cobranza humanizada' },
      ],
      marquee: [
        'Hasta 36 cuotas con boleto',
        'Análisis de aprobación',
        'Cobranza humanizada',
        'Pago garantizado',
        'Contrato con firma digital',
        'Sin usar el límite de la tarjeta',
      ],
      skipIntro: 'Saltar apertura',
    },
    legal: {
      prefix: 'Al continuar aceptas los',
      terms: 'Terminos',
      middle: 'y la',
      privacy: 'Politica de privacidad',
      suffix: '.',
    },
  },
};

export function getLoginKitMessages(locale: Locale): LoginKitMessages {
  return messages[locale] ?? messages['pt-BR'];
}
